import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import Layout from "./components/Layout";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import VehiclesPage from "./pages/VehiclesPage";
import DriversPage from "./pages/DriversPage";
import PlanningPage from "./pages/PlanningPage";
import RecettesPage from "./pages/RecettesPage";
import RepairsPage from "./pages/RepairsPage";
import AlertsPage from "./pages/AlertsPage";
import InvestorDashboard from "./pages/InvestorDashboard";
import InvestorRecettesPage from "./pages/InvestorRecettesPage";
import InvestorRepairsPage from "./pages/InvestorRepairsPage";
import InvestorPiecesPage from "./pages/InvestorPiecesPage";
import DriverPortal from "./pages/DriverPortal";
import ReportsPage from "./pages/ReportsPage";
import QuotesPage from "./pages/QuotesPage";
import InvestorsPage from "./pages/InvestorsPage";
import GestionnairesPage from "./pages/GestionnairesPage";
import AdminsPage from "./pages/AdminsPage";
import DebtManagementPage from "./pages/DebtManagementPage";
import PiecesPage from "./pages/PiecesPage";

function getRedirectPath(role) {
  if (role === "investisseur") return "/investisseur";
  if (role === "chauffeur") return "/chauffeur-portal";
  return "/dashboard";
}

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="text-slate-500">Chargement...</div></div>;
  if (!user) return <Navigate to="/login" />;
  if (roles && !roles.includes(user.role)) return <Navigate to={getRedirectPath(user.role)} />;
  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="text-slate-500">Chargement...</div></div>;

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={getRedirectPath(user.role)} /> : <LoginPage />} />
      <Route path="/" element={<Navigate to={user ? getRedirectPath(user.role) : "/login"} />} />
      <Route path="/dashboard" element={<ProtectedRoute roles={["admin", "gestionnaire"]}><DashboardPage /></ProtectedRoute>} />
      <Route path="/vehicules" element={<ProtectedRoute roles={["admin", "gestionnaire"]}><VehiclesPage /></ProtectedRoute>} />
      <Route path="/investisseurs" element={<ProtectedRoute roles={["admin", "gestionnaire"]}><InvestorsPage /></ProtectedRoute>} />
      <Route path="/gestionnaires" element={<ProtectedRoute roles={["admin"]}><GestionnairesPage /></ProtectedRoute>} />
      <Route path="/administrateurs" element={<ProtectedRoute roles={["admin"]}><AdminsPage /></ProtectedRoute>} />
      <Route path="/chauffeurs" element={<ProtectedRoute roles={["admin", "gestionnaire"]}><DriversPage /></ProtectedRoute>} />
      <Route path="/planning" element={<ProtectedRoute roles={["admin", "gestionnaire"]}><PlanningPage /></ProtectedRoute>} />
      <Route path="/recettes" element={<ProtectedRoute roles={["admin", "gestionnaire"]}><RecettesPage /></ProtectedRoute>} />
      <Route path="/dettes" element={<ProtectedRoute roles={["admin", "gestionnaire"]}><DebtManagementPage /></ProtectedRoute>} />
      <Route path="/reparations" element={<ProtectedRoute roles={["admin", "gestionnaire"]}><RepairsPage /></ProtectedRoute>} />
      <Route path="/pieces" element={<ProtectedRoute roles={["admin", "gestionnaire"]}><PiecesPage /></ProtectedRoute>} />
      <Route path="/alertes" element={<ProtectedRoute roles={["admin", "gestionnaire"]}><AlertsPage /></ProtectedRoute>} />
      <Route path="/devis" element={<ProtectedRoute roles={["admin", "gestionnaire", "investisseur"]}><QuotesPage /></ProtectedRoute>} />
      <Route path="/bilans" element={<ProtectedRoute roles={["admin", "gestionnaire", "investisseur"]}><ReportsPage /></ProtectedRoute>} />
      <Route path="/logs" element={<ProtectedRoute roles={["admin"]}><DashboardPage /></ProtectedRoute>} />
      <Route path="/investisseur" element={<ProtectedRoute roles={["investisseur"]}><InvestorDashboard /></ProtectedRoute>} />
      <Route path="/investisseur/recettes" element={<ProtectedRoute roles={["investisseur"]}><InvestorRecettesPage /></ProtectedRoute>} />
      <Route path="/investisseur/reparations" element={<ProtectedRoute roles={["investisseur"]}><InvestorRepairsPage /></ProtectedRoute>} />
      <Route path="/investisseur/pieces" element={<ProtectedRoute roles={["investisseur"]}><InvestorPiecesPage /></ProtectedRoute>} />
      <Route path="/chauffeur-portal" element={<ProtectedRoute roles={["chauffeur"]}><DriverPortal /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}
